/* header-system.js — поведение новой шапки AMITEA.
 *
 * Модули: sticky-бар (скролл вниз — спрятать, вверх — компактная версия),
 * ховер-меню с панелями, поиск-панель (недавние запросы + просмотренные
 * товары + живые подсказки через Predictive Search API + skeleton),
 * мобильный drawer, языковой тултип с таймером-кольцом, зеркалирование
 * счётчика корзины в sticky-бар.
 *
 * Все хуки — data-атрибуты (data-ds-*), без визуальных классов как API.
 * Инициализация идемпотентна: customElements.define выполняется один раз,
 * а connectedCallback чистит за собой в disconnectedCallback (Theme Editor
 * пересоздаёт секцию при изменении настроек).
 */

(function () {
  'use strict';

  var LS = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
      } catch (e) {
        return fallback;
      }
    },
    set: function (key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        /* приватный режим — молча живём без памяти */
      }
    },
    remove: function (key) {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {}
    },
  };

  var SS = {
    get: function (key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set: function (key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {}
    },
  };

  var KEYS = {
    recentSearches: 'ds_recent_searches',
    recentProducts: 'ds_recent_products',
    langChoice: 'ds_lang_choice',
    visited: 'ds_visited',
    tipSeen: 'ds_lang_tip_seen',
    suggestMode: 'ds_suggest_mode',
    langRedirected: 'ds_lang_redirected',
  };

  if (customElements.get('sticky-header')) return;

  var StickyHeader = function () {
    return Reflect.construct(HTMLElement, [], StickyHeader);
  };
  StickyHeader.prototype = Object.create(HTMLElement.prototype);
  StickyHeader.prototype.constructor = StickyHeader;
  Object.setPrototypeOf(StickyHeader, HTMLElement);

  StickyHeader.prototype.connectedCallback = function () {
    this.main = this.querySelector('[data-ds-header-main]');
    this.stickyBar = this.querySelector('[data-ds-sticky]');
    this.search = this.querySelector('[data-ds-search]');
    this.overlay = this.querySelector('[data-ds-overlay]');
    this.drawer = this.querySelector('[data-ds-drawer]');
    this.langTip = this.querySelector('[data-ds-lang-tip]');
    this.abortCtrl = null;
    this.searchTimer = null;
    this.preventReveal = false;

    this.setHeaderHeight();
    this.initSticky();
    this.initNav();
    this.initSearch();
    this.initDrawer();
    this.initLang();
    this.initCartMirror();
    this.recordViewedProduct();

    document.documentElement.classList.add('ds-header-ready');
  };

  StickyHeader.prototype.disconnectedCallback = function () {
    if (this.onScrollBound) window.removeEventListener('scroll', this.onScrollBound);
    if (this.onKeyBound) document.removeEventListener('keydown', this.onKeyBound);
    if (this.onDocClickBound) document.removeEventListener('click', this.onDocClickBound);
    if (this.cartObserver) this.cartObserver.disconnect();
    document.body.classList.remove('ds-scroll-lock');
  };

  /* ---------- размеры ---------- */

  StickyHeader.prototype.setHeaderHeight = function () {
    var h = this.main ? Math.round(this.main.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--header-height', h + 'px');
    if (this.stickyBar) {
      var sh = Math.round(this.stickyBar.getBoundingClientRect().height) || 56;
      this.style.setProperty('--ds-sticky-height', sh + 'px');
    }
  };

  /* ---------- sticky ---------- */

  StickyHeader.prototype.initSticky = function () {
    var self = this;
    this.addEventListener('preventHeaderReveal', function () {
      self.preventReveal = true;
    });
    if (!this.stickyBar) return;

    var lastY = window.scrollY;
    this.onScrollBound = function () {
      var y = window.scrollY;
      var headerBottom = self.main ? self.main.offsetTop + self.main.offsetHeight : 200;
      if (y <= headerBottom) {
        self.hideSticky();
        self.preventReveal = false;
      } else if (y > lastY + 2) {
        self.hideSticky();
      } else if (y < lastY - 2) {
        if (self.preventReveal) {
          self.preventReveal = false;
        } else {
          self.showSticky();
        }
      }
      lastY = y;
    };
    window.addEventListener('scroll', this.onScrollBound, { passive: true });
  };

  StickyHeader.prototype.showSticky = function () {
    if (this.classList.contains('is-search-open')) return;
    this.stickyBar.classList.add('is-visible');
    this.stickyBar.setAttribute('aria-hidden', 'false');
    this.stickyBar.querySelectorAll('a, button').forEach(function (el) {
      el.removeAttribute('tabindex');
    });
  };

  StickyHeader.prototype.hideSticky = function () {
    if (!this.stickyBar) return;
    this.stickyBar.classList.remove('is-visible');
    this.stickyBar.setAttribute('aria-hidden', 'true');
    this.closeNavPanels(this.stickyBar);
  };

  /* ---------- навигация ---------- */

  StickyHeader.prototype.initNav = function () {
    var self = this;
    var hoverable = window.matchMedia('(hover: hover)');

    this.querySelectorAll('[data-ds-nav-details]').forEach(function (details) {
      var summary = details.querySelector('summary');
      var item = details.closest('.ds-nav-item');
      var closeTimer = null;

      summary.addEventListener('click', function (e) {
        // details toggles сам; синхронизируем aria и закрываем соседей
        window.setTimeout(function () {
          summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
          if (details.open) self.closeNavPanels(details.closest('header, [data-ds-sticky]'), details);
        }, 0);
      });

      if (hoverable.matches) {
        item.addEventListener('mouseenter', function () {
          window.clearTimeout(closeTimer);
          if (!details.open) {
            self.closeNavPanels(details.closest('header, [data-ds-sticky]'), details);
            details.open = true;
            summary.setAttribute('aria-expanded', 'true');
          }
        });
        item.addEventListener('mouseleave', function () {
          closeTimer = window.setTimeout(function () {
            details.open = false;
            summary.setAttribute('aria-expanded', 'false');
          }, 120);
        });
      }

      details.addEventListener('focusout', function (e) {
        if (!details.contains(e.relatedTarget)) {
          details.open = false;
          summary.setAttribute('aria-expanded', 'false');
        }
      });
    });

    this.onKeyBound = function (e) {
      if (e.key !== 'Escape') return;
      if (self.drawer && self.drawer.classList.contains('is-open')) {
        self.closeDrawer();
      } else if (self.classList.contains('is-search-open')) {
        self.closeSearch();
      } else {
        self.closeNavPanels(self);
      }
    };
    document.addEventListener('keydown', this.onKeyBound);

    this.onDocClickBound = function (e) {
      if (!self.contains(e.target)) self.closeNavPanels(self);
    };
    document.addEventListener('click', this.onDocClickBound);
  };

  StickyHeader.prototype.closeNavPanels = function (scope, except) {
    (scope || this).querySelectorAll('[data-ds-nav-details]').forEach(function (d) {
      if (d !== except && d.open) {
        d.open = false;
        var s = d.querySelector('summary');
        if (s) s.setAttribute('aria-expanded', 'false');
      }
    });
  };

  /* ---------- поиск ---------- */

  StickyHeader.prototype.initSearch = function () {
    var self = this;
    if (!this.search) return;

    this.suggestMode = SS.get(KEYS.suggestMode) || this.dataset.suggestMode || 'predictive';
    this.inputs = Array.prototype.slice.call(this.search.querySelectorAll('[data-ds-search-input]'));
    this.resultsEl = this.search.querySelector('[data-ds-search-results]');
    this.skeletonEl = this.search.querySelector('[data-ds-search-skeleton]');
    this.idleEl = this.search.querySelector('[data-ds-search-idle]');

    this.querySelectorAll('[data-ds-search-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (self.classList.contains('is-search-open')) {
          self.closeSearch();
          return;
        }
        self.openSearch(btn.hasAttribute('data-ds-search-from-sticky'));
      });
    });
    this.querySelectorAll('[data-ds-search-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeSearch();
      });
    });
    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.closeSearch();
        self.closeDrawer();
      });
    }

    this.inputs.forEach(function (input) {
      var form = input.closest('[data-ds-search-form]');
      var clearBtn = form.querySelector('[data-ds-search-clear]');

      input.addEventListener('input', function () {
        self.inputs.forEach(function (other) {
          if (other !== input) other.value = input.value;
        });
        self.search.querySelectorAll('[data-ds-search-clear]').forEach(function (b) {
          b.hidden = input.value.length === 0;
        });
        self.scheduleSuggest(input.value);
      });

      form.addEventListener('submit', function () {
        self.saveRecentSearch(input.value);
      });

      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          self.inputs.forEach(function (i) {
            i.value = '';
          });
          self.search.querySelectorAll('[data-ds-search-clear]').forEach(function (b) {
            b.hidden = true;
          });
          self.showIdle();
          input.focus();
        });
      }
    });

    // клик по «Все результаты …» в подгруженных подсказках — запомнить запрос
    this.resultsEl.addEventListener('click', function (e) {
      var all = e.target.closest('[data-ds-search-all]');
      if (all) self.saveRecentSearch(self.inputs[0] ? self.inputs[0].value : '');
    });

    var clearRecent = this.search.querySelector('[data-ds-recent-clear]');
    if (clearRecent) {
      clearRecent.addEventListener('click', function () {
        LS.remove(KEYS.recentSearches);
        self.renderRecent();
      });
    }

    this.initRail();
  };

  StickyHeader.prototype.openSearch = function (fromSticky) {
    // если шапка уже прокручена — открываем в режиме sticky-бара,
    // чтобы панель была видна «ниже меню», а не под уехавшей шапкой
    var headerBottom = this.main ? this.main.offsetTop + this.main.offsetHeight : 0;
    var stickyMode = !!fromSticky || window.scrollY > headerBottom;
    if (stickyMode && this.stickyBar) this.showSticky();

    this.classList.add('is-search-open');
    this.classList.toggle('is-sticky-search', stickyMode);
    this.search.hidden = false;
    this.fitSearchHeight();
    this.showOverlay();
    this.renderRecent();
    this.renderViewed();
    this.querySelectorAll('[data-ds-search-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'true');
    });
    if (window.matchMedia('(max-width: 989px)').matches) {
      document.body.classList.add('ds-scroll-lock');
    }
    var visible = this.inputs.filter(function (i) {
      return i.offsetParent !== null;
    })[0];
    if (visible) visible.focus();
  };

  /* панель не выше экрана: остаток скроллится внутри */
  StickyHeader.prototype.fitSearchHeight = function () {
    var top = this.search.getBoundingClientRect().top;
    this.search.style.maxHeight = Math.max(240, window.innerHeight - top - 8) + 'px';
  };

  StickyHeader.prototype.closeSearch = function () {
    if (!this.classList.contains('is-search-open')) return;
    this.classList.remove('is-search-open', 'is-sticky-search');
    this.search.hidden = true;
    this.search.style.maxHeight = '';
    this.hideOverlay();
    document.body.classList.remove('ds-scroll-lock');
    this.querySelectorAll('[data-ds-search-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
  };

  StickyHeader.prototype.showOverlay = function () {
    if (!this.overlay) return;
    this.overlay.hidden = false;
    var el = this.overlay;
    window.requestAnimationFrame(function () {
      el.classList.add('is-visible');
    });
  };

  StickyHeader.prototype.hideOverlay = function () {
    if (!this.overlay) return;
    this.overlay.classList.remove('is-visible');
    var el = this.overlay;
    window.setTimeout(function () {
      el.hidden = true;
    }, 200);
  };

  StickyHeader.prototype.showIdle = function () {
    // чипсы/недавние/лента всегда видимы — прячем только выдачу
    if (this.resultsEl) this.resultsEl.hidden = true;
    if (this.skeletonEl) this.skeletonEl.hidden = true;
    if (this.abortCtrl) this.abortCtrl.abort();
    window.clearTimeout(this.searchTimer);
  };

  StickyHeader.prototype.scheduleSuggest = function (query) {
    var self = this;
    window.clearTimeout(this.searchTimer);
    var q = (query || '').trim();
    if (q.length < 2) {
      this.showIdle();
      return;
    }
    // skeleton сразу — и при начале ввода, и при каждом изменении запроса
    // (макет 5b: 4 строки-заглушки на время ожидания выдачи);
    // выдача живёт между полем и чипсами, чипсы остаются видимыми
    this.resultsEl.hidden = true;
    this.skeletonEl.hidden = false;
    this.searchTimer = window.setTimeout(function () {
      self.fetchSuggest(q);
    }, 250);
  };

  /* Адрес источника подсказок.
   *
   * Обычный источник — Predictive Search API. Он поддерживает ограниченный
   * список языков, иврита в нём нет: /he/search/suggest отвечает 417
   * «Unsupported buyer locale» (docs: Ajax Predictive Search → Supported
   * languages). Поэтому для таких локалей берём обычный поиск через Section
   * Rendering — он работает на всех языках, только чуть медленнее.
   *
   * Режим приходит из Liquid (data-suggest-mode), но если предиктивный
   * источник всё же откажет — переключаемся на запасной на лету и
   * запоминаем выбор на сессию, чтобы не бить в стену на каждый запрос.
   */
  StickyHeader.prototype.suggestUrl = function (q) {
    if (this.suggestMode === 'fallback') {
      var searchUrl =
        window.routes && window.routes.search_url ? window.routes.search_url : '/search';
      return (
        searchUrl +
        '?q=' +
        encodeURIComponent(q) +
        '&type=product&options%5Bprefix%5D=last&section_id=ds-search-fallback'
      );
    }
    var base =
      window.routes && window.routes.predictive_search_url
        ? window.routes.predictive_search_url
        : '/search/suggest';
    return (
      base +
      '?q=' +
      encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=6&section_id=ds-search-suggest'
    );
  };

  StickyHeader.prototype.fetchSuggest = function (q) {
    var self = this;
    if (this.abortCtrl) this.abortCtrl.abort();
    this.abortCtrl = new AbortController();

    this.resultsEl.hidden = true;
    this.skeletonEl.hidden = false;

    var url = this.suggestUrl(q);

    fetch(url, { signal: this.abortCtrl.signal })
      .then(function (r) {
        if (!r.ok) {
          if (self.suggestMode !== 'fallback') {
            self.suggestMode = 'fallback';
            SS.set(KEYS.suggestMode, 'fallback');
            return null; // повторим запрос уже запасным источником
          }
          throw new Error(r.status);
        }
        return r.text();
      })
      .then(function (html) {
        if (html === null) {
          self.fetchSuggest(q);
          return null;
        }
        return html;
      })
      .then(function (html) {
        if (html === null) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-ds-suggest-inner]');
        self.resultsEl.innerHTML = inner ? inner.innerHTML : '';
        // shimmer под фото, пока картинка результата не догрузилась
        self.resultsEl.querySelectorAll('img').forEach(function (img) {
          if (img.complete) return;
          img.classList.add('ds-img-loading');
          var done = function () {
            img.classList.remove('ds-img-loading');
          };
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
        self.skeletonEl.hidden = true;
        self.resultsEl.hidden = false;
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        self.skeletonEl.hidden = true;
        self.showIdle();
      });
  };

  /* --- недавние запросы --- */

  StickyHeader.prototype.saveRecentSearch = function (q) {
    q = (q || '').trim();
    if (q.length < 2) return;
    var list = LS.get(KEYS.recentSearches, []);
    list = list.filter(function (item) {
      return item.toLowerCase() !== q.toLowerCase();
    });
    list.unshift(q);
    LS.set(KEYS.recentSearches, list.slice(0, 5));
  };

  StickyHeader.prototype.renderRecent = function () {
    var wrap = this.search.querySelector('[data-ds-search-recent]');
    var listEl = this.search.querySelector('[data-ds-recent-list]');
    if (!wrap || !listEl) return;
    var list = LS.get(KEYS.recentSearches, []).slice(0, 3);
    if (!list.length) {
      wrap.hidden = true;
      return;
    }
    var searchUrl =
      window.routes && window.routes.search_url ? window.routes.search_url : '/search';
    listEl.textContent = '';
    list.forEach(function (q) {
      var a = document.createElement('a');
      a.className = 'ds-search__recent-item';
      a.href = searchUrl + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '13');
      svg.setAttribute('height', '13');
      svg.setAttribute('aria-hidden', 'true');
      var use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
      use.setAttribute('href', '#ds-icon-clock');
      svg.appendChild(use);
      a.appendChild(svg);
      a.appendChild(document.createTextNode(q));
      listEl.appendChild(a);
    });
    wrap.hidden = false;
  };

  /* --- просмотренные товары + слайдер --- */

  StickyHeader.prototype.recordViewedProduct = function () {
    var dataEl = this.querySelector('[data-ds-viewed-product]');
    if (!dataEl) return;
    var item;
    try {
      item = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!item || !item.url) return;
    var list = LS.get(KEYS.recentProducts, []);
    list = list.filter(function (p) {
      return p.url !== item.url;
    });
    list.unshift(item);
    LS.set(KEYS.recentProducts, list.slice(0, 8));
  };

  StickyHeader.prototype.renderViewed = function () {
    var rail = this.search.querySelector('[data-ds-search-rail]');
    var track = this.search.querySelector('[data-ds-rail-track]');
    if (!rail || !track || rail.dataset.dsViewedDone) return;
    rail.dataset.dsViewedDone = '1';

    var viewed = LS.get(KEYS.recentProducts, []);
    if (!viewed.length) return;

    var existing = {};
    track.querySelectorAll('[data-ds-card-url]').forEach(function (card) {
      existing[card.getAttribute('data-ds-card-url')] = true;
    });

    var frag = document.createDocumentFragment();
    viewed = viewed.slice(0, 8);
    viewed.forEach(function (p) {
      if (existing[p.url]) return;
      var a = document.createElement('a');
      a.className = 'ds-search-card ds-card-viewed';
      a.href = p.url;
      a.setAttribute('data-ds-card-url', p.url);
      var img = document.createElement('img');
      img.className = 'ds-search-card__img';
      img.loading = 'lazy';
      img.alt = p.title || '';
      if (p.image) img.src = p.image;
      var body = document.createElement('div');
      body.className = 'ds-search-card__body';
      var title = document.createElement('div');
      title.className = 'ds-search-card__title';
      title.textContent = p.title || '';
      var price = document.createElement('div');
      price.className = 'ds-search-card__price';
      price.textContent = p.price || '';
      if (p.variant) {
        var vlabel = document.createElement('span');
        vlabel.className = 'ds-search-card__variant';
        vlabel.textContent = p.variant;
        price.appendChild(vlabel);
      }
      body.appendChild(title);
      body.appendChild(price);
      a.appendChild(img);
      a.appendChild(body);
      frag.appendChild(a);
    });
    track.insertBefore(frag, track.firstChild);

    // просмотренных достаточно — популярные не подмешиваем; иначе дополняем
    // ими ленту, но не длиннее 8 карточек суммарно
    var popular = track.querySelectorAll('[data-ds-card-url]:not(.ds-card-viewed)');
    var keepPopular = viewed.length >= 5 ? 0 : Math.max(0, 8 - viewed.length);
    popular.forEach(function (card, i) {
      if (i >= keepPopular) card.remove();
    });

    if (track.children.length) rail.hidden = false;
    this.updateRailButtons();
  };

  StickyHeader.prototype.initRail = function () {
    var self = this;
    var track = this.search.querySelector('[data-ds-rail-track]');
    if (!track) return;
    var prev = this.search.querySelector('[data-ds-rail-prev]');
    var next = this.search.querySelector('[data-ds-rail-next]');
    var dir = document.documentElement.getAttribute('dir') === 'rtl' ? -1 : 1;
    if (prev)
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -384 * dir, behavior: 'smooth' });
      });
    if (next)
      next.addEventListener('click', function () {
        track.scrollBy({ left: 384 * dir, behavior: 'smooth' });
      });
    track.addEventListener(
      'scroll',
      function () {
        self.updateRailButtons();
      },
      { passive: true }
    );
    this.updateRailButtons();
  };

  StickyHeader.prototype.updateRailButtons = function () {
    var track = this.search.querySelector('[data-ds-rail-track]');
    var prev = this.search.querySelector('[data-ds-rail-prev]');
    var next = this.search.querySelector('[data-ds-rail-next]');
    if (!track || !prev || !next) return;
    var max = track.scrollWidth - track.clientWidth - 2;
    var pos = Math.abs(track.scrollLeft);
    prev.disabled = pos <= 2;
    next.disabled = pos >= max;
  };

  /* ---------- drawer ---------- */

  StickyHeader.prototype.initDrawer = function () {
    var self = this;
    if (!this.drawer) return;

    this.querySelectorAll('[data-ds-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.openDrawer(btn);
      });
    });
    this.drawer.querySelectorAll('[data-ds-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.closeDrawer();
      });
    });
    this.drawer.querySelectorAll('[data-ds-drawer-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var group = btn.closest('[data-ds-drawer-group]');
        var open = group.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  };

  StickyHeader.prototype.openDrawer = function (opener) {
    this.drawerOpener = opener || null;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ds-scroll-lock');
    this.querySelectorAll('[data-ds-drawer-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'true');
    });
    var first = this.drawer.querySelector('.ds-drawer__panel a, .ds-drawer__panel button');
    if (first) first.focus();
  };

  StickyHeader.prototype.closeDrawer = function () {
    if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ds-scroll-lock');
    this.querySelectorAll('[data-ds-drawer-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
    if (this.drawerOpener) this.drawerOpener.focus();
  };

  /* ---------- язык ---------- */

  StickyHeader.prototype.initLang = function () {
    var self = this;
    var currentLocale = this.dataset.locale || 'ru';

    // клик по пилюле = осознанный выбор языка
    this.querySelectorAll('[data-ds-lang-opt]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        LS.set(KEYS.langChoice, opt.getAttribute('data-ds-lang-opt'));
        SS.set(KEYS.langRedirected, '1');
      });
    });

    LS.set(KEYS.visited, true);

    var choice = LS.get(KEYS.langChoice, null);

    // запомненный язык: тихий возврат на выбранную локаль при заходе извне
    if (
      choice &&
      choice !== currentLocale &&
      !SS.get(KEYS.langRedirected) &&
      document.referrer.indexOf(window.location.host) === -1
    ) {
      SS.set(KEYS.langRedirected, '1');
      var path = window.location.pathname;
      var target = null;
      if (choice === 'he' && path.indexOf('/he') !== 0) {
        target = '/he' + (path === '/' ? '' : path);
      } else if (choice === 'ru' && path.indexOf('/he') === 0) {
        target = path === '/he' ? '/' : path.replace(/^\/he\//, '/');
      }
      if (target) {
        window.location.replace(target + window.location.search + window.location.hash);
        return;
      }
    }
    SS.set(KEYS.langRedirected, '1');

    // тултип
    if (!this.langTip || this.dataset.langTipEnabled !== 'true') return;
    if (choice || SS.get(KEYS.tipSeen)) return;

    var firstVisit = !LS.get(KEYS.visited + '_prev', false);
    LS.set(KEYS.visited + '_prev', true);
    var browserHe = (navigator.language || '').toLowerCase().indexOf('he') === 0;
    var mismatch = (currentLocale === 'he') !== browserHe;

    if (!firstVisit && !mismatch) return;

    var tip = this.langTip;
    var secs = parseFloat(getComputedStyle(tip).getPropertyValue('--ds-lang-tip-secs')) || 25;

    tip.hidden = false;
    window.requestAnimationFrame(function () {
      tip.classList.add('is-counting');
    });

    var hideTimer = window.setTimeout(dismiss, secs * 1000);

    function dismiss() {
      window.clearTimeout(hideTimer);
      tip.hidden = true;
      tip.classList.remove('is-counting');
      SS.set(KEYS.tipSeen, '1');
    }

    tip.querySelector('[data-ds-lang-tip-close]').addEventListener('click', dismiss);
    tip.querySelector('[data-ds-lang-confirm]').addEventListener('click', function () {
      LS.set(KEYS.langChoice, currentLocale);
      dismiss();
    });
    var switchLink = tip.querySelector('[data-ds-lang-switch]');
    if (switchLink) {
      switchLink.addEventListener('click', function () {
        LS.set(KEYS.langChoice, switchLink.getAttribute('data-target-locale'));
        SS.set(KEYS.langRedirected, '1');
        SS.set(KEYS.tipSeen, '1');
      });
    }
  };

  /* ---------- зеркало счётчика корзины в sticky ---------- */

  StickyHeader.prototype.initCartMirror = function () {
    var source = this.querySelector('#cart-icon-bubble');
    var mirrors = this.querySelectorAll('[data-ds-cart-mirror]');
    if (!source || !mirrors.length) return;
    var sync = function () {
      mirrors.forEach(function (m) {
        m.innerHTML = source.innerHTML;
      });
    };
    this.cartObserver = new MutationObserver(sync);
    this.cartObserver.observe(source, { childList: true, subtree: true });
  };

  customElements.define('sticky-header', StickyHeader);
})();
